import React, { useState, useEffect } from 'react';
import { Loader2, Users, TrendingUp, Clock, AlertTriangle, DollarSign, Search, Download, ShieldAlert } from 'lucide-react';
import { debtorsReportAPI } from '../../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const DebtorsReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('outstanding');

  useEffect(() => {
    const fetch = async () => {
      try {
        const result = await debtorsReportAPI.getReport();
        setData(result);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /><span className="ml-2 text-sm text-gray-500">Loading debtors report...</span></div>;
  if (!data) return <p className="text-sm text-gray-500 text-center py-12">No data available</p>;

  const { summary, debtors } = data;

  const ageColors = { '0-30': 'bg-green-100 text-green-700', '31-60': 'bg-amber-100 text-amber-700', '61-90': 'bg-orange-100 text-orange-700', '90+': 'bg-red-100 text-red-700' };

  let filtered = debtors.filter(d => {
    if (filter === 'repeat' && !d.is_repeat) return false;
    if (filter === 'advance' && !d.is_advance) return false;
    if (filter === 'credit' && !d.is_credit) return false;
    if (filter === 'exceeded' && !d.credit_exceeded) return false;
    if (filter === 'has_limit' && !d.credit_limit) return false;
    if (search && !d.customer_name.toLowerCase().includes(search.toLowerCase()) && !(d.salesperson_name || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  filtered.sort((a, b) => {
    if (sortBy === 'credit_exceeded') return (b.credit_exceeded ? 1 : 0) - (a.credit_exceeded ? 1 : 0) || b.outstanding - a.outstanding;
    return (b[sortBy] || 0) - (a[sortBy] || 0);
  });

  const fmtAmount = (v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v?.toLocaleString() || '0';

  const exportToExcel = () => {
    const rows = filtered.map(d => ({
      'Customer': d.customer_name,
      'Type': d.type,
      'State': d.state,
      'Email': d.email || '',
      'Phone': d.phone || '',
      'Payment Terms': d.payment_terms || `${d.credit_days}d credit`,
      'Credit Limit': d.credit_limit || 'No limit',
      'Available Credit': d.available_credit ?? 'N/A',
      'Credit Exceeded': d.credit_exceeded ? 'YES' : 'No',
      'Over Limit By': d.over_limit_amount || 0,
      'Salesperson': d.salesperson_name || '-',
      'Orders': d.order_count,
      'Repeat': d.is_repeat ? 'Yes' : 'No',
      'Total Business': d.total_business,
      'Outstanding': d.outstanding,
      'Ageing Days': d.ageing_days,
      'Ageing Bucket': d.ageing_bucket,
      'Last Order Date': d.last_order_date ? new Date(d.last_order_date).toLocaleDateString('en-IN') : '-',
    }));

    rows.push({});
    rows.push({
      'Customer': 'SUMMARY',
      'Credit Limit': summary.total_credit_limit || 0,
      'Credit Exceeded': `${summary.credit_exceeded_count || 0} customers`,
      'Over Limit By': summary.total_over_limit || 0,
      'Orders': filtered.reduce((s, d) => s + d.order_count, 0),
      'Total Business': filtered.reduce((s, d) => s + d.total_business, 0),
      'Outstanding': filtered.reduce((s, d) => s + d.outstanding, 0),
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 30 }, { wch: 12 }, { wch: 14 }, { wch: 24 }, { wch: 14 },
      { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      { wch: 18 }, { wch: 8 }, { wch: 8 },
      { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Debtors Report');

    const ageRows = [
      { 'Bucket': '0-30 days', 'Amount': summary.ageing_0_30 },
      { 'Bucket': '31-60 days', 'Amount': summary.ageing_31_60 },
      { 'Bucket': '61-90 days', 'Amount': summary.ageing_61_90 },
      { 'Bucket': '90+ days', 'Amount': summary.ageing_90_plus },
      { 'Bucket': 'TOTAL', 'Amount': summary.total_outstanding },
    ];
    const ws2 = XLSX.utils.json_to_sheet(ageRows);
    ws2['!cols'] = [{ wch: 16 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Ageing Summary');

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Debtors_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div data-testid="debtors-report" className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Invoiced', value: fmtAmount(summary.total_invoiced || summary.total_outstanding), icon: Users, color: '#24549c' },
          { label: 'Total Received', value: fmtAmount(summary.total_received || 0), icon: TrendingUp, color: '#059669' },
          { label: 'DN / CN Net', value: `+${fmtAmount(summary.total_debit_notes || 0)} / -${fmtAmount(summary.total_credit_notes || 0)}`, icon: Clock, color: '#d97706' },
          { label: 'Outstanding', value: fmtAmount(summary.total_outstanding), icon: DollarSign, color: '#C62828' },
          { label: 'Credit Exceeded', value: summary.credit_exceeded_count || 0, icon: ShieldAlert, color: summary.credit_exceeded_count > 0 ? '#C62828' : '#9CA3AF', highlight: summary.credit_exceeded_count > 0 },
        ].map((s, i) => (
          <div key={i} className={`bg-white rounded-xl border p-4 flex items-center gap-3 ${s.highlight ? 'border-red-200 bg-red-50/30' : 'border-gray-100'}`}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
              <s.icon className="w-4 h-4" style={{ color: s.color }} />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{s.value}</p>
              <p className="text-[10px] text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Credit Limit Alert */}
      {summary.credit_exceeded_count > 0 && (
        <div className="p-3 rounded-xl border border-red-200 bg-red-50/50 flex items-start gap-3" data-testid="credit-exceeded-alert">
          <ShieldAlert className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-red-700">
              {summary.credit_exceeded_count} customer{summary.credit_exceeded_count > 1 ? 's have' : ' has'} exceeded credit limit
            </p>
            <p className="text-[10px] text-red-500 mt-0.5">
              Total over-limit exposure: INR {fmtAmount(summary.total_over_limit)}. New OCs for these customers will trigger a warning.
            </p>
          </div>
          <button onClick={() => setFilter('exceeded')} className="ml-auto px-3 py-1 text-[10px] font-semibold bg-red-100 text-red-700 rounded-lg hover:bg-red-200 whitespace-nowrap" data-testid="show-exceeded-btn">
            Show Exceeded
          </button>
        </div>
      )}

      {/* Ageing Analysis */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <h3 className="text-xs font-bold text-gray-900 mb-3">Ageing Analysis</h3>
        <div className="grid grid-cols-4 gap-3">
          {[
            { bucket: '0-30 days', value: summary.ageing_0_30, color: 'bg-green-500' },
            { bucket: '31-60 days', value: summary.ageing_31_60, color: 'bg-amber-500' },
            { bucket: '61-90 days', value: summary.ageing_61_90, color: 'bg-orange-500' },
            { bucket: '90+ days', value: summary.ageing_90_plus, color: 'bg-red-500' },
          ].map((a, i) => (
            <div key={i} className="text-center">
              <div className={`h-2 rounded-full ${a.color} mb-2`} style={{ width: `${Math.min(100, (a.value / Math.max(summary.total_outstanding, 1)) * 100)}%`, minWidth: '8px' }} />
              <p className="text-sm font-bold text-gray-900">{fmtAmount(a.value)}</p>
              <p className="text-[10px] text-gray-500">{a.bucket}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="debtors-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by customer or salesperson..." className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <div className="flex gap-1.5">
          {[
            { id: 'all', label: 'All' },
            { id: 'repeat', label: 'Repeat' },
            { id: 'advance', label: 'Advance' },
            { id: 'credit', label: 'Credit' },
            { id: 'has_limit', label: 'Has Limit' },
            { id: 'exceeded', label: 'Exceeded', danger: true },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${
                filter === f.id
                  ? f.danger ? 'text-white bg-red-600' : 'text-white'
                  : f.danger ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
              }`}
              style={filter === f.id && !f.danger ? { backgroundColor: '#24549c' } : {}}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-3 py-2 text-xs border border-gray-200 rounded-lg">
          <option value="outstanding">Sort: Outstanding</option>
          <option value="order_count">Sort: Order Count</option>
          <option value="total_business">Sort: Total Business</option>
          <option value="ageing_days">Sort: Ageing</option>
          <option value="credit_exceeded">Sort: Credit Risk</option>
        </select>
        <button onClick={exportToExcel} data-testid="export-debtors-excel" className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold text-white rounded-lg transition-all hover:opacity-90" style={{ backgroundColor: '#0d6b3e' }}>
          <Download className="w-3.5 h-3.5" />Export to Excel
        </button>
      </div>

      {/* Debtors Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {['Customer', 'Type', 'Invoiced', 'Received', 'DN/CN', 'Outstanding', 'Credit Limit', 'Credit Status', 'Ageing', 'Last Order'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.customer_id}
                  className={`border-b border-gray-50 hover:bg-gray-50/50 ${d.credit_exceeded ? 'bg-red-50/30' : ''}`}
                  data-testid={`debtor-row-${d.customer_id}`}>
                  <td className="px-4 py-3">
                    <p className="text-xs font-medium text-gray-900">{d.customer_name}</p>
                    <p className="text-[10px] text-gray-400">{d.state} {d.email && `| ${d.email}`}</p>
                  </td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${d.type === 'Government' ? 'bg-blue-50 text-blue-700' : d.type === 'Corporate' ? 'bg-emerald-50 text-emerald-700' : 'bg-purple-50 text-purple-700'}`}>{d.type}</span></td>
                  <td className="px-4 py-3 text-xs font-semibold text-gray-900">{fmtAmount(d.total_invoiced || d.total_business)}</td>
                  <td className="px-4 py-3 text-xs font-semibold text-green-600">{d.total_received > 0 ? fmtAmount(d.total_received) : '-'}</td>
                  <td className="px-4 py-3 text-[10px]">
                    {(d.debit_notes > 0 || d.credit_notes > 0) ? (
                      <span>
                        {d.debit_notes > 0 && <span className="text-red-500">+{fmtAmount(d.debit_notes)}</span>}
                        {d.debit_notes > 0 && d.credit_notes > 0 && ' / '}
                        {d.credit_notes > 0 && <span className="text-green-500">-{fmtAmount(d.credit_notes)}</span>}
                      </span>
                    ) : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-xs font-bold" style={{ color: d.outstanding > 0 ? '#C62828' : '#0d6b3e' }}>{d.outstanding > 0 ? fmtAmount(d.outstanding) : '-'}</td>
                  <td className="px-4 py-3">
                    {d.credit_limit > 0 ? (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-900">{fmtAmount(d.credit_limit)}</p>
                        <p className={`text-[9px] ${d.credit_exceeded ? 'text-red-500 font-semibold' : 'text-green-600'}`}>
                          {d.credit_exceeded ? `Over by ${fmtAmount(d.over_limit_amount)}` : `Avail: ${fmtAmount(d.available_credit)}`}
                        </p>
                      </div>
                    ) : <span className="text-[10px] text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    {d.credit_limit > 0 ? (
                      d.credit_exceeded ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-100 text-red-700" data-testid={`credit-badge-${d.customer_id}`}>
                          <ShieldAlert className="w-3 h-3" /> EXCEEDED
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold text-green-600">OK</span>
                      )
                    ) : <span className="text-[10px] text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    {d.outstanding > 0 ? (
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${ageColors[d.ageing_bucket] || 'bg-gray-100'}`}>
                        {d.ageing_days}d ({d.ageing_bucket})
                      </span>
                    ) : <span className="text-xs text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-[10px] text-gray-500">{d.last_order_date ? new Date(d.last_order_date).toLocaleDateString('en-IN') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 text-[10px] text-gray-500">
          Showing {filtered.length} of {debtors.length} customers | Outstanding: {fmtAmount(filtered.reduce((s, d) => s + d.outstanding, 0))}
          {filter === 'exceeded' && ` | Over-limit: ${fmtAmount(filtered.reduce((s, d) => s + (d.over_limit_amount || 0), 0))}`}
        </div>
      </div>
    </div>
  );
};

export default DebtorsReport;
